import { z } from 'zod';
import { FractionSchema, IdSchema, InstantSchema, LocalDateSchema, MoneySchema } from '../common/index.js';

/** Biens, titres, estimation des parts (équipe seulement), interrogations notariales (architecture §2.4). */

export const MENTION_ESTIMATION =
  'Estimation indicative, ne vaut pas évaluation. Le partage effectif dépend des actes et des choix de la famille.' as const;

export const AssetSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    kind: z.enum(['immeuble_bati', 'terrain', 'compte', 'assurance_vie', 'vehicule', 'creance', 'dette']),
    label: z.string().min(1),
    cadastralRef: z.string().nullable(),
    commune: z.string().nullable(),
    areaM2: z.number().int().min(0).nullable(),
    pluZoning: z.string().nullable(),
    occupancy: z.enum(['occupe_heritier', 'vacant', 'loue']).nullable(),
    titleStatus: z.enum(['trouve', 'recherche', 'introuvable', 'prescription']),
    valuation: z.object({ low: MoneySchema, high: MoneySchema, source: z.enum(['comparables', 'expertise', 'agence']) }).nullable(),
  })
  .meta({ id: 'Asset', description: 'Bien du dossier.' });

export const AssetCreateSchema = AssetSchema.omit({ id: true, dossierId: true, valuation: true }).meta({ id: 'AssetCreate' });

export const ScenarioSchema = z.enum(['vendre', 'partager', 'sci', 'constructible']).meta({ id: 'Scenario' });

export const EstimateSchema = z
  .object({
    dossierId: IdSchema,
    scenario: ScenarioSchema,
    mention: z.literal(MENTION_ESTIMATION),
    visibility: z.literal('equipe'),
    perHeir: z.array(
      z.object({ personId: IdSchema, displayName: z.string().min(1), share: FractionSchema, low: MoneySchema, high: MoneySchema }),
    ),
    reserve: MoneySchema,
    fees: MoneySchema,
    reliability: z.object({
      score: z.number().int().min(0).max(100),
      factors: z.array(z.object({ label: z.string().min(1), impact: z.enum(['positif', 'negatif', 'neutre']) })),
    }),
    computedAt: InstantSchema,
  })
  .meta({ id: 'Estimate', description: 'Estimation des parts à l’instant T, réservée à l’équipe MAAT.' });

export const NotaryInquirySchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    successionId: IdSchema.nullable(),
    kind: z.enum(['FCDDV', 'FICOBA', 'FICOVIE', 'DGFiP', 'CGSS', 'SPF_immeuble', 'SPF_personne', 'syndic']),
    status: z.enum(['a_envoyer', 'envoyee', 'revenue']),
    sentAt: LocalDateSchema.nullable(),
    returnedAt: LocalDateSchema.nullable(),
    documentId: IdSchema.nullable(),
    rowVersion: z.number().int().min(0),
  })
  .meta({ id: 'NotaryInquiry', description: 'Interrogation notariale (checklist de la clerc).' });

export const NotaryInquiryPatchSchema = z
  .object({
    status: z.enum(['a_envoyer', 'envoyee', 'revenue']).optional(),
    sentAt: LocalDateSchema.nullable().optional(),
    returnedAt: LocalDateSchema.nullable().optional(),
    documentId: IdSchema.nullable().optional(),
  })
  .meta({ id: 'NotaryInquiryPatch' });

export const RenunciationSchema = z
  .object({
    id: IdSchema,
    successionId: IdSchema,
    personId: IdSchema,
    filedAt: LocalDateSchema.nullable(),
    court: z.enum(['TJ Pointe-à-Pitre', 'TJ Basse-Terre', 'notaire']),
    attestationReceivedAt: LocalDateSchema.nullable(),
  })
  .meta({ id: 'Renunciation' });
